import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Brand, CreateBrandInput, UpdateBrandInput } from "../types";

const BRAND_FIELDS = `
  id
  organizationId
  name
  code
  description
  logoUrl
  logoKey
  website
  status
  createdAt
  updatedAt
`;

export const brandApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrands: builder.query<Brand[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Brands($organizationId: String) {
              brands(organizationId: $organizationId) {
                ${BRAND_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Brand[]>(res, "brands"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((b) => ({ type: "Brand" as const, id: b.id })),
              { type: "Brand" as const, id: "LIST" },
            ]
          : [{ type: "Brand" as const, id: "LIST" }],
    }),

    getBrand: builder.query<Brand, string>({
      query: (id) => ({
        body: {
          query: `
            query Brand($id: String!) {
              brand(id: $id) {
                ${BRAND_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Brand>(res, "brand"),

      providesTags: (_result, _err, id) => [{ type: "Brand", id }],
    }),

    createBrand: builder.mutation<Brand, CreateBrandInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateBrand($input: CreateBrandInput!) {
              createBrand(input: $input) {
                ${BRAND_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Brand>(res, "createBrand"),

      invalidatesTags: [{ type: "Brand", id: "LIST" }],
    }),

    updateBrand: builder.mutation<
      Brand,
      { id: string; input: UpdateBrandInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateBrand($id: String!, $input: UpdateBrandInput!) {
              updateBrand(id: $id, input: $input) {
                ${BRAND_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Brand>(res, "updateBrand"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Brand", id },
        { type: "Brand", id: "LIST" },
      ],
    }),

    deleteBrand: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteBrand($id: String!) {
              deleteBrand(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteBrand"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Brand", id },
        { type: "Brand", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetBrandsQuery,
  useLazyGetBrandsQuery,
  useGetBrandQuery,
  useLazyGetBrandQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
} = brandApi;
